import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { UrlTreeNode } from './selection';
import {
  clearSiteMapDefer,
  forgetSiteMap,
  isSiteMapEmpty,
  isSiteMapPendingFresh,
  SITEMAP_CONSUMED_KEY,
  SITEMAP_PENDING_KEY,
  SITEMAP_RESUME_KEY,
  siteMapDeferRemainingMs,
  siteMapKey,
  siteMapSnoozeState,
  siteMapSnoozeUntil,
  snoozeSiteMap,
} from './siteMapFlags';
import { QueryKey } from '@/Config/api/queryKey';
import { Favicon } from '@/Domain/components/Shared/Favicon/Favicon';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { cn } from '@/Domain/utils/cn';

interface SiteMapPreview {
  status: 'none' | 'mapping' | 'ready' | 'failed';
  total_urls: number;
  host: string;
  tree: UrlTreeNode | Record<string, never>;
  error_message: string | null;
}

const readFlag = (key: string) =>
  typeof window !== 'undefined' && localStorage.getItem(key) != null;

// App-wide bottom-right dock that narrates the sitewide scrape and owns the open
// logic: when the map is ready it grows the review modal out of itself, except
// during onboarding where it waits for the celebration window (siteMapDeferRemainingMs).
export const SiteScrapeStatusDock = () => {
  const { t } = useTranslation(['knowledge_base']);
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  const isReviewOpen = useModalStore((store) =>
    store.isOpen(MODAL_NAMES.SITE_SCRAPE_REVIEW_MODAL_ID),
  );
  const toggleModal = useModalStore((store) => store.toggleModal);
  const { capture } = usePostHogAnalytics();
  // The snooze lives in localStorage; re-render once it's set.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const [hiddenOnKnowledgeBase, setHiddenOnKnowledgeBase] = useState(false);

  const onOnboarding = pathname.includes('getting-started');
  const onKnowledgeBase = pathname.includes('knowledge-base');
  const flagged = readFlag(SITEMAP_PENDING_KEY) || readFlag(SITEMAP_RESUME_KEY);

  useEffect(() => {
    if (!onKnowledgeBase) setHiddenOnKnowledgeBase(false);
  }, [onKnowledgeBase]);

  // Poll only with an active map to track or on the KB page (where resume matters),
  // never for users who have no scrape running.
  const enabled = flagged || onKnowledgeBase;

  const { data } = useQuery<SiteMapPreview>({
    queryKey: [QueryKey.knowledgeBase.siteMapPreview.get],
    enabled,
    staleTime: 15_000,
    // Beyond the default poll-while-mapping: a freshly-flagged kickoff can race
    // the backend creating the row (onboarding Step 11 runs after "completed"),
    // so briefly keep polling through the idle 404 until the map appears.
    refetchInterval: (query) => {
      const currentStatus = (query.state.data as SiteMapPreview | undefined)?.status;
      if (currentStatus === 'mapping') return 2000;
      if ((currentStatus === undefined || currentStatus === 'none') && isSiteMapPendingFresh()) {
        return 3000;
      }
      return false;
    },
  });

  const discardMutation = useMutation<unknown, Error, void>({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.delete],
    onSuccess: () => {
      forgetSiteMap();
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.get] });
      toast.success(t('tabs.website.review.discard_toast'));
      capture(PostHogEventEnum.SiteScrapeDiscarded, { source: 'dock' });
    },
  });

  const status = data?.status ?? 'none';
  const host = data?.host ?? '';
  const tree = (data?.tree && 'path' in data.tree ? data.tree : null) as UrlTreeNode | null;
  const mapKey = tree ? siteMapKey(tree, data?.total_urls ?? 0) : null;
  const consumed =
    typeof window !== 'undefined' &&
    mapKey !== null &&
    localStorage.getItem(SITEMAP_CONSUMED_KEY) === mapKey;
  const count = data?.total_urls ?? 0;

  // Empty map (dead/blocked domain, or a legacy ready+0 row): nothing to defer or auto-open.
  const mapEmpty = isSiteMapEmpty(status, count, data?.error_message);

  // Defer opening on the onboarding route and through the celebration window it sets.
  const deferred = onOnboarding || siteMapDeferRemainingMs() > 0;

  // After "Not now" the map never auto-opens again; for 24h it's offered only on the Knowledge Base.
  const snoozeState = mapKey ? siteMapSnoozeState(mapKey) : 'none';
  const snoozeUntil = mapKey ? siteMapSnoozeUntil(mapKey) : null;
  // Ready maps stop polling, so nothing else re-renders an idle tab when the snooze runs out.
  useEffect(() => {
    if (snoozeState !== 'active' || snoozeUntil === null) return;
    const timer = setTimeout(rerender, Math.max(0, snoozeUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [snoozeState, snoozeUntil]);
  const snoozeHidden =
    status === 'ready' && snoozeState === 'active' && (!onKnowledgeBase || hiddenOnKnowledgeBase);

  // Auto-open the review modal once per ready map, growing it out of the dock.
  const autoOpenedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (status !== 'ready' || mapEmpty || !mapKey || consumed || isReviewOpen || !flagged) return;
    if (onOnboarding || snoozeState !== 'none') return;
    if (autoOpenedKeyRef.current === mapKey) return;
    const wait = siteMapDeferRemainingMs();
    const openNow = () => {
      autoOpenedKeyRef.current = mapKey;
      clearSiteMapDefer();
      toggleModal(MODAL_NAMES.SITE_SCRAPE_REVIEW_MODAL_ID, { trigger: 'auto' });
    };
    if (wait <= 0) {
      openNow();
      return;
    }
    const timer = setTimeout(openNow, wait);
    return () => clearTimeout(timer);
  }, [
    status,
    mapEmpty,
    mapKey,
    consumed,
    isReviewOpen,
    flagged,
    onOnboarding,
    snoozeState,
    toggleModal,
  ]);

  const analysing = status === 'mapping';
  // Ready but held back (mid-onboarding, or the celebration window right after
  // it): say so instead of pretending the analysis is still running — a user
  // parked on Getting Started would otherwise watch a spinner forever.
  const readyDeferred = status === 'ready' && deferred && !mapEmpty;
  const reviewable = status === 'ready' && !deferred && !mapEmpty;
  // Surface failures (and empty maps, with copy naming the real problem) instead of vanishing mid-analysing; the user dismisses to clear the dock.
  const failed = status === 'failed' || mapEmpty;
  const visible =
    (analysing || readyDeferred || reviewable || failed) &&
    !consumed &&
    !isReviewOpen &&
    !snoozeHidden;

  const handleSnooze = () => {
    if (!mapKey) return;
    snoozeSiteMap(mapKey);
    capture(PostHogEventEnum.SiteScrapeSnoozed, {
      source: 'dock',
      total_urls: count,
      on_knowledge_base: onKnowledgeBase,
    });
    toast.success(t('tabs.website.review.snooze_toast'));
    // The Knowledge Base still offers a snoozed map, so hide it there until the user leaves.
    if (onKnowledgeBase) setHiddenOnKnowledgeBase(true);
    rerender();
  };

  const hasHost = host.length > 0;
  const title = failed
    ? hasHost
      ? host
      : t('tabs.website.review.dock_analysing')
    : hasHost
      ? host
      : analysing
        ? t('tabs.website.review.dock_analysing')
        : t('tabs.website.review.found', { count });
  const subtitle = failed
    ? t(mapEmpty ? 'tabs.website.review.map_empty' : 'tabs.website.review.map_failed')
    : analysing
      ? t('tabs.website.review.dock_analysing_hint')
      : readyDeferred
        ? onOnboarding
          ? t('tabs.website.review.dock_ready_deferred_hint', { count })
          : t('tabs.website.review.found', { count })
        : hasHost
          ? t('tabs.website.review.found', { count })
          : t('tabs.website.review.dock_ready_hint');

  return (
    <TooltipProvider>
      <AnimatePresence>
        {visible && (
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.div
                className="fixed bottom-6 right-6 z-40 w-[20rem] max-w-[calc(100vw-3rem)]"
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 24, scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 320, damping: 26 }}
                data-testid="site-scrape-status-dock"
              >
                <div className="relative overflow-hidden rounded-2xl border border-border bg-card/95 p-4 shadow-xl backdrop-blur-md">
                  {/* Soft animated sheen along the top while analysing. */}
                  {analysing && (
                    <motion.div
                      aria-hidden
                      className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-foreground/40 to-transparent"
                      initial={{ x: '-100%' }}
                      animate={{ x: '100%' }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  )}

                  {/* Not now (ready) / discard (failed), tucked into the corner clear of the text. */}
                  {reviewable && (
                    <button
                      type="button"
                      onClick={handleSnooze}
                      aria-label={t('tabs.website.review.dock_snooze')}
                      title={t('tabs.website.review.dock_snooze')}
                      className="absolute right-2 top-2 flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      data-testid="site-scrape-status-dock-snooze"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                  {failed && (
                    <button
                      type="button"
                      onClick={() => discardMutation.mutate()}
                      disabled={discardMutation.isPending}
                      aria-label={t('tabs.website.review.dismiss')}
                      className="absolute right-2 top-2 flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-50"
                      data-testid="site-scrape-status-dock-dismiss"
                    >
                      {discardMutation.isPending ? (
                        <Spinner className="size-3.5" />
                      ) : (
                        <X className="size-3.5" />
                      )}
                    </button>
                  )}

                  <div className="flex items-center gap-3">
                    <motion.div
                      className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-background"
                      animate={analysing ? { scale: [1, 1.06, 1] } : { scale: 1 }}
                      transition={
                        analysing
                          ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut' }
                          : { duration: 0.3 }
                      }
                    >
                      <Favicon url={host} className="size-5 rounded" />
                    </motion.div>

                    <div className={cn('min-w-0 flex-1', (reviewable || failed) && 'pr-5')}>
                      <p className="truncate text-sm font-medium text-foreground">{title}</p>
                      <p
                        className={cn(
                          'text-xs',
                          // Let failure/empty recovery copy wrap (no CTA to a full-text view); truncate the rest.
                          failed ? 'text-destructive' : 'truncate text-muted-foreground',
                        )}
                      >
                        {subtitle}
                      </p>
                    </div>

                    {analysing && <Spinner className="size-4 shrink-0 text-muted-foreground" />}
                  </div>

                  {reviewable && (
                    <button
                      type="button"
                      onClick={() =>
                        toggleModal(MODAL_NAMES.SITE_SCRAPE_REVIEW_MODAL_ID, { trigger: 'dock' })
                      }
                      className="mt-3 flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-foreground py-2 text-xs font-medium text-background transition-transform hover:scale-[1.02] active:scale-[0.98]"
                      data-testid="site-scrape-status-dock-review"
                    >
                      {t('tabs.website.review.resume_cta')}
                      <ArrowRight className="size-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            </TooltipTrigger>
            {analysing && (
              <TooltipContent side="top" variant="dark" className="max-w-[240px] text-center">
                {t('tabs.website.review.dock_analysing_tooltip')}
              </TooltipContent>
            )}
            {readyDeferred && onOnboarding && (
              <TooltipContent side="top" variant="dark" className="max-w-[240px] text-center">
                {t('tabs.website.review.dock_ready_deferred_tooltip')}
              </TooltipContent>
            )}
          </Tooltip>
        )}
      </AnimatePresence>
    </TooltipProvider>
  );
};
